import React, { useState, useRef, useEffect } from "react";
import {
  IconoirProvider,
  Menu,
  Home,
  WashingMachine,
  EmojiTalkingHappy,
  Bicycle,
  Shirt,
  ModernTv,
  SleeperChair,
  SmallLampAlt,
  Tree,
  MusicDoubleNote,
  Neighbourhood,
  Fish,
  HomeSimpleDoor,
  Basketball,
  Settings,
  Gamepad,
  Check,
  NavArrowDown,
} from "iconoir-react";

interface FilterOption {
  id: string;
  label: string;
  icon: string;
}

interface PriceOption {
  id: string;
  label: string;
}

interface TimeOption {
  id: string;
  label: string;
}

interface MarketplaceFilterProps {
  onClose: () => void;
  onApply: (filters: Record<string, unknown>) => void;
}

export const MarketplaceFilter: React.FC<MarketplaceFilterProps> = ({
  onClose,
  onApply,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [priceSort, setPriceSort] = useState<string>("asc");
  const [timeFilter, setTimeFilter] = useState<string>("newest");
  const [showPriceOptions, setShowPriceOptions] = useState(false);
  const [showTimeOptions, setShowTimeOptions] = useState(false);

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const categories: FilterOption[] = [
    { id: "all", label: "Tất cả", icon: "check" },
    { id: "Đồ gia dụng", label: "Đồ gia dụng", icon: "home" },
    { id: "Máy móc động cơ", label: "Máy móc động cơ", icon: "tool" },
    { id: "Đồ dùng trẻ em", label: "Đồ dùng trẻ em", icon: "baby" },
    {
      id: "Phương tiện di chuyển",
      label: "Phương tiện di chuyển",
      icon: "car",
    },
    { id: "Quần áo & phụ kiện", label: "Quần áo & phụ kiện", icon: "shirt" },
    { id: "Đồ điện", label: "Đồ điện", icon: "zap" },
    { id: "Nội thất", label: "Nội thất", icon: "sofa" },
    { id: "Đồ trang trí nhà cửa", label: "Đồ trang trí nhà cửa", icon: "lamp" },
    { id: "Cây cối", label: "Cây cối", icon: "flower" },
    { id: "Nhạc cụ", label: "Nhạc cụ", icon: "music" },
    {
      id: "Dịch vụ hàng xóm cung cấp",
      label: "Dịch vụ hàng xóm cung cấp",
      icon: "helping-hand",
    },
    { id: "Thú cưng", label: "Thú cưng", icon: "cat" },
    {
      id: "Thuê mặt bằng, đồ đạc",
      label: "Thuê mặt bằng, đồ đạc",
      icon: "building",
    },
    {
      id: "Thể thao ngoài trời",
      label: "Thể thao ngoài trời",
      icon: "dumbbell",
    },
    { id: "Đồ nghề", label: "Đồ nghề", icon: "palette" },
    { id: "Đồ chơi", label: "Đồ chơi", icon: "gamepad" },
  ];

  const priceOptions: PriceOption[] = [
    { id: "asc", label: "Thấp đến cao" },
    { id: "desc", label: "Cao đến thấp" },
  ];

  const timeOptions: TimeOption[] = [
    { id: "newest", label: "Mới nhất" },
    { id: "oldest", label: "Cũ nhất" },
  ];

  const handleApply = () => {
    onApply({
      category: selectedCategory,
      priceSort,
      timeFilter,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-60 bg-black/50 flex items-end">
      <div
        ref={modalRef}
        className="w-full px-4 pt-3 pb-20 bg-[#FBF6F1] rounded-tl-md rounded-tr-md inline-flex flex-col justify-start items-center animate-in slide-in-from-bottom duration-300 max-h-[90vh] overflow-y-auto modal-scroll-hidden"
      >
        {/* Handle bar at top */}
        <div className="w-20 h-[5px]  rounded-[100px]" />

        {/* Categories section */}
        <div className="self-stretch py-2 flex flex-col justify-start items-start gap-3">
          <div className="justify-start text-[#1E3A3A] text-xl font-medium font-manrope leading-normal">
            Loại sản phẩm
          </div>

          <div className="self-stretch px-4 flex flex-col justify-start items-start gap-3">
            {categories.map((category) => (
              <div
                key={category.id}
                className="self-stretch inline-flex justify-between items-start cursor-pointer"
                onClick={() => setSelectedCategory(category.id)}
              >
                <div className="flex justify-start items-center gap-2">
                  <div className="size-4 relative overflow-hidden">
                    <IconoirProvider
                      iconProps={{
                        color: "#1E3A3A",
                        strokeWidth: 1.2,
                        width: "16px",
                        height: "16px",
                      }}
                    >
                      {category.icon === "check" && <Menu />}
                      {category.icon === "home" && <Home />}
                      {category.icon === "tool" && <WashingMachine />}
                      {category.icon === "baby" && <EmojiTalkingHappy />}
                      {category.icon === "car" && <Bicycle />}
                      {category.icon === "shirt" && <Shirt />}
                      {category.icon === "zap" && <ModernTv />}
                      {category.icon === "sofa" && <SleeperChair />}
                      {category.icon === "lamp" && <SmallLampAlt />}
                      {category.icon === "flower" && <Tree />}
                      {category.icon === "music" && <MusicDoubleNote />}
                      {category.icon === "helping-hand" && <Neighbourhood />}
                      {category.icon === "cat" && <Fish />}
                      {category.icon === "building" && <HomeSimpleDoor />}
                      {category.icon === "dumbbell" && <Basketball />}
                      {category.icon === "palette" && <Settings />}
                      {category.icon === "gamepad" && <Gamepad />}
                    </IconoirProvider>
                  </div>
                  <div className="justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">
                    {category.label}
                  </div>
                </div>
                {selectedCategory === category.id && (
                  <div className="size-4 relative overflow-hidden">
                    <IconoirProvider
                      iconProps={{
                        color: "#1E3A3A",
                        strokeWidth: 1.2,
                        width: "16px",
                        height: "16px",
                      }}
                    >
                      <Check />
                    </IconoirProvider>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Price section with dropdown */}
        <div className="self-stretch py-2 inline-flex justify-between items-center relative">
          <div className="justify-start text-[#1E3A3A] text-xl font-medium font-manrope leading-normal">
            Giá cả
          </div>
          <div
            className="px-4 py-2.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] flex justify-start items-center gap-2.5 cursor-pointer"
            onClick={() => setShowPriceOptions(!showPriceOptions)}
          >
            <div className="flex justify-start items-center gap-2.5">
              <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">
                {priceOptions.find((option) => option.id === priceSort)
                  ?.label || "Chọn"}
              </div>
            </div>
            <div className="size-4 relative overflow-hidden">
              <IconoirProvider
                iconProps={{
                  color: "#1E3A3A",
                  strokeWidth: 1.2,
                  width: "16px",
                  height: "16px",
                }}
              >
                <NavArrowDown />
              </IconoirProvider>
            </div>
          </div>

          {/* Price options dropdown */}
          {showPriceOptions && (
            <div className="bg-[#FBF6F1] rounded-md border border-[#D1D1D0] overflow-hidden absolute z-80 right-0 top-12 w-48">
              {priceOptions.map((option) => (
                <div
                  key={option.id}
                  className="p-3 cursor-pointer flex justify-between items-center"
                  onClick={() => {
                    setPriceSort(option.id);
                    setShowPriceOptions(false);
                  }}
                >
                  <span className="font-sora text-sm font-normal text-[#1E3A3A]">
                    {option.label}
                  </span>
                  {priceSort === option.id && (
                    <IconoirProvider
                      iconProps={{
                        color: "#1E3A3A",
                        strokeWidth: 1.2,
                        width: "16px",
                        height: "16px",
                      }}
                    >
                      <Check />
                    </IconoirProvider>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Time section with dropdown */}
        <div className="self-stretch py-2 inline-flex justify-between items-center relative">
          <div className="justify-start text-[#1E3A3A] text-xl font-medium font-manrope leading-normal">
            Thời gian
          </div>
          <div
            className="px-4 py-2.5 rounded-md outline outline-1 outline-offset-[-1px] outline-[#D1D1D0] flex justify-start items-center gap-2.5 cursor-pointer"
            onClick={() => setShowTimeOptions(!showTimeOptions)}
          >
            <div className="text-center justify-start text-[#1E3A3A] text-sm font-normal font-sora leading-none">
              {timeOptions.find((option) => option.id === timeFilter)?.label ||
                "Chọn"}
            </div>
            <div className="size-4 relative overflow-hidden">
              <IconoirProvider
                iconProps={{
                  color: "#1E3A3A",
                  strokeWidth: 1.2,
                  width: "16px",
                  height: "16px",
                }}
              >
                <NavArrowDown />
              </IconoirProvider>
            </div>
          </div>

          {/* Time options dropdown */}
          {showTimeOptions && (
            <div className="bg-[#FBF6F1] rounded-md border border-[#D1D1D0] overflow-hidden absolute z-80 right-0 top-12 w-48">
              {timeOptions.map((option) => (
                <div
                  key={option.id}
                  className="p-3 cursor-pointer flex justify-between items-center"
                  onClick={() => {
                    setTimeFilter(option.id);
                    setShowTimeOptions(false);
                  }}
                >
                  <span className="font-sora text-sm font-normal text-[#1E3A3A]">
                    {option.label}
                  </span>
                  {timeFilter === option.id && (
                    <IconoirProvider
                      iconProps={{
                        color: "#1E3A3A",
                        strokeWidth: 1.2,
                        width: "16px",
                        height: "16px",
                      }}
                    >
                      <Check />
                    </IconoirProvider>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Save button */}
        <div className="w-full px-4 pt-8 flex flex-col justify-start items-start gap-2.5">
          <button
            data-hierarchy="primary"
            data-show-icon-line-left="false"
            data-show-icon-line-right="false"
            data-size="medium"
            className="self-stretch px-4 py-3.5 bg-[#E06448] rounded-full inline-flex justify-center items-center gap-2.5"
            onClick={handleApply}
          >
            <div className="text-center justify-start text-[#F4E7DA] text-sm font-medium font-sora leading-none">
              Lưu
            </div>
          </button>
        </div>

        {/* Bottom indicator - hidden in scrollable view */}
      </div>
    </div>
  );
};

export default MarketplaceFilter;
